/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../app/store';
import { fetchMediaResistances, fetchNextMediaResistances, resetMediaResistancePage } from './MediaResistanceSlice';
import { useTranslation } from '../../hooks/useTranslation';
import { InfoButtonPopup, Row } from 'festo-react-ui-kit';
import { resistanceTypeImg } from '../../components/ResistantTypeImg';
import { LoadingIndicator } from '@festo-ui/react';
import Parser from 'html-react-parser';
import { buildQuery, getQueryParam, updateURLQuery } from '../../components/QueryBuilder';
import { RequestHandler } from '../../app/RequestHandler';
import { econdaClickEvent } from '../../Econda';
import './MediaResistance.scss';
import ExportExcel from '../../components/exports/ExportExcel';
import ExportPDF from '../../components/exports/ExportPDF';
import SortBy from '../../components/SortBy/SortBy';
import NoResultsFound from '../../components/NoResultsFound/NoResultsFound';
import ChipContainer, { Filter } from '../filter/chip/container/ChipContainer';
import { getFilters } from '../filter/filterSlice';
import {
  prepareFilterResistance,
  prepareMediaFilter,
  prepareMaterialFilter,
  prepareMaterialCategoryFilter,
} from '../filter/filterUtils';
import { getConcentrationUnit } from "../../helper";

export const SORT_ASCENDING = 'ASC';
export const SORT_DESCENDING = 'DESC';

export enum TYPES_OF_SORTING {
  MEDIA = 'MEDIA',
  MATERIAL = 'MATERIAL',
}

export function buildMediaResistanceQuery(parameters: any, resistanceType: any) {
  const sortBy = getQueryParam('sortBy', TYPES_OF_SORTING.MATERIAL);
  const sortDirection = getQueryParam('sortDirection', SORT_ASCENDING);

  let resistance = '';
  if (resistanceType !== 'ALL') {
    resistance = resistanceType;
  }

  parameters['sortBy'] = sortBy;
  parameters['sortDirection'] = sortDirection;
  parameters['resistance'] = resistance;

  return buildQuery(parameters);
}

interface MediaResistanceProps {
  language: string;
}

const MediaResistance = React.forwardRef<HTMLDivElement, MediaResistanceProps>(({ language }, tableSectionRef) => {
  const [screenWidth, setScreenWidth] = useState<number | null>(null);
  const [filters, setFilters] = useState<Filter[]>([]);

  const [isFilterLoading, setIsFilterLoading] = useState(true);
  const mediaResistances = useSelector((state: RootState) => state.mediaResistances);
  const materialCategories = useSelector((state: RootState) => state.materialCategories);
  const medias = useSelector((state: RootState) => state.medias);
  const materials = useSelector((state: RootState) => state.materials);
  const resistanceTypes = useSelector((state: RootState) => state.resistanceTypes);

  const selectedFilters = useSelector(getFilters);

  const dispatch = useDispatch();
  const t = useTranslation();
  const infoButtonConditionallyResistanceType = 'infoButtonConditionallyResistanceType';

  const [sortBy, setSortby] = useState(getQueryParam('sortBy', TYPES_OF_SORTING.MATERIAL));
  const [sortDirection, setSortDirection] = useState(getQueryParam('sortDirection', SORT_ASCENDING));
  const [prevQuery, setPrevQuery] = useState('');

  const requestHandler = new RequestHandler();

  const resistanceMap = new Map();

  resistanceTypes.values.forEach((resistanceType) => {
    resistanceMap.set(resistanceType.resistance, resistanceType.translationKey);
  });

  const [isDownloadPDF, setIsDownloadPDF] = useState(false);
  const [isDownloadExcel, setIsDownloadExcel] = useState(false);

  useEffect(() => {
    const allFilters: Filter[] = [];
    const resistanceFilters = prepareFilterResistance(selectedFilters.filterResistanceType, resistanceTypes.label);
    const mediaFilters = prepareMediaFilter(selectedFilters.mediaFilter, medias.label);
    const materialFilters = prepareMaterialFilter(selectedFilters.materialFilter, materials.label);
    const materialCategoryFilters = prepareMaterialCategoryFilter(
      selectedFilters.materialCategoryFilter,
      materialCategories.label,
    );

    if (
      resistanceFilters.length > 0 ||
      mediaFilters.length > 0 ||
      materialFilters.length > 0 ||
      materialCategoryFilters.length > 0
    ) {
      allFilters.push(...mediaFilters, ...materialCategoryFilters, ...materialFilters, ...resistanceFilters);
    } else {
      allFilters.splice(0, allFilters.length);
    }

    setFilters(allFilters);
  }, [materialCategories.label, materials.label, medias.label, resistanceTypes.label, selectedFilters]);

  useEffect(() => {
    if (materials.values.length === 0 || medias.values.length === 0 || materialCategories.values.length === 0) {
      setIsFilterLoading(true);
    } else {
      setIsFilterLoading(false);
    }
  }, [materials.values, medias.values, materialCategories.values]);

  useEffect(() => {
    if (!isFilterLoading) {
      updateURLQuery('sortBy', sortBy);
      updateURLQuery('sortDirection', sortDirection);

      const query = buildMediaResistanceQuery(
        {
          language: language,
          mediaIds: medias.selectedIds,
          materialIds: materials.selectedIds,
          materialCategoryIds: materialCategories.selectedIds,
          page: '0',
          size: '50',
        },
        resistanceTypes.selected,
      );

      if (query !== prevQuery) {
        dispatch(resetMediaResistancePage());
        setPrevQuery(query);
        dispatch(fetchMediaResistances(query, false));
        const table: Element | null = document.getElementById('media-resistance-grid');
        if (table) {
          table.scrollTop = 0;
        }
      }
    }
  }, [
    materialCategories.selectedIds,
    materials.selectedIds,
    medias.selectedIds,
    resistanceTypes.selected,
    sortBy,
    sortDirection,
    isFilterLoading,
    language,
    prevQuery,
    dispatch,
  ]);

  useEffect(() => {
    function handleWidth() {
      setScreenWidth(window.innerWidth);
    }
    // Add event listener
    window.addEventListener('resize', handleWidth);
    // Call handler right away so state gets updated with initial window size
    handleWidth();
    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', handleWidth);
  }, []);

  useEffect(() => {
    if (
      !mediaResistances.isLoading &&
      mediaResistances.getNextPage &&
      mediaResistances.nextPage !== undefined &&
      mediaResistances.nextPage !== ''
    ) {
      dispatch(fetchMediaResistances('', false, mediaResistances.nextPage));
    }
  }, [dispatch, mediaResistances.getNextPage, mediaResistances.isLoading, mediaResistances.nextPage]);

  useEffect(() => {
    if (
      !mediaResistances.isLoading &&
      mediaResistances.getPrevPage &&
      mediaResistances.previousPage !== '' &&
      mediaResistances.nextPage !== ''
    ) {
      dispatch(fetchMediaResistances('', false, mediaResistances.previousPage));
    }
  }, [
    dispatch,
    mediaResistances.getPrevPage,
    mediaResistances.isLoading,
    mediaResistances.nextPage,
    mediaResistances.previousPage,
  ]);

  function showIcon(resistance: string) {
    return resistanceTypeImg[resistance]['SIZE_24'];
  }

  function handleClick(e: any) {
    if (e.target.classList[0] === 'MEDIA') {
      econdaClickEvent('media', 'table', 'filter', 'asc/desc');
    } else {
      econdaClickEvent('material', 'table', 'filter', 'asc/desc');
    }

    if (sortBy === e.target.classList[0]) {
      if (sortDirection === SORT_ASCENDING) {
        setSortDirection(SORT_DESCENDING);
      } else {
        setSortDirection(SORT_ASCENDING);
      }
    } else {
      setSortby(e.target.classList[0]);
      setSortDirection(SORT_ASCENDING);
    }
  }

  function formatFormula(formula: string) {
    const formattedFormula = formula ? formula.replace(/(\d+)/g, '$1'.sub()) : '-';
    return Parser(formattedFormula);
  }

  function formatTemperature(temperature: any) {
    if (temperature !== '') {
      temperature = temperature + ' °C';
    } else {
      temperature = temperature + '-';
    }
    return temperature;
  }

  function formatConcentration(concentration: any, translationKey: string) {
    const concentrationNumber = Number(concentration);
    if (!Number.isNaN(concentrationNumber) && concentration !== '') {
      concentration += ` ${getConcentrationUnit(translationKey)}`;
    } else {
      concentration += '-';
    }
    return concentration;
  }

  const handleScroll = (event: any) => {
    const element = event.target;
    const scrollDifference = element.scrollHeight - element.scrollTop;
    if (
      scrollDifference <= element.clientHeight + 100 &&
      !mediaResistances.getNextPage &&
      mediaResistances.nextPage !== ''
    ) {
      dispatch(fetchNextMediaResistances());
    }
  };

  return (
    <>
      <div className="app-container full-screen">
        <div className="container-full">
          <header className="mb-5 header-border" ref={tableSectionRef}>
            <ChipContainer filterList={filters} />
            <h3 className="row header-flex">
              <div
                id="media-resistance-data-header-title"
                className={`col-xs-7 ${screenWidth && screenWidth < 300 ? 'pr-0' : ''}`}
              >
                <div id="header1">
                  {t('mediaResistanceData')} {screenWidth && screenWidth < 575 && <br />}{' '}
                </div>
                <div id="header2" className="number-results-style one-line">
                  {!mediaResistances.isLoading &&
                    new Intl.NumberFormat(
                      requestHandler.language.toLowerCase() + '-' + requestHandler.country.toUpperCase(),
                    ).format(mediaResistances.totalElements) +
                      ' ' +
                      t('unitResults')}
                </div>
              </div>
              <div id="media-resistance-data-header-download" className="col-xs-5">
                <div className="padding-right-on-pdf">
                  <ExportPDF language={language} isDisabled={isDownloadExcel} isDownloadPDF={isDownloadPDF} setIsDownloadPDF={setIsDownloadPDF} />
                </div>
                <div>
                  <ExportExcel language={language} isDownloadExcel={isDownloadExcel} setIsDownloadExcel={setIsDownloadExcel}  isDisabled={isDownloadPDF} />
                </div>
              </div>
            </h3>
          </header>
        </div>
        <div className="sort-by-container">
          <SortBy
            sortBy={sortBy}
            sortDirection={sortDirection}
            setSortby={setSortby}
            setSortDirection={setSortDirection}
          />
        </div>

        <div id="media-resistance-grid" className="my-custom-scrollbar fwe-scrollbar" onScroll={handleScroll}>
          <Row className="media-resistance-table-header">
            <div className="col hidden-on-375 table-header">
              <div
                className={
                  sortBy === 'MEDIA'
                    ? sortDirection === SORT_ASCENDING
                      ? 'MEDIA order media-order-active'
                      : 'MEDIA order media-order-active-order-descending'
                    : 'MEDIA order'
                }
                onClick={handleClick}
              >
                {t('mediaDatagridHeader')}
                {sortDirection === SORT_ASCENDING ? (
                  <span className="MEDIA fwe-icon fwe-icon-arrows-expand" />
                ) : (
                  <span className="MEDIA fwe-icon fwe-icon-arrows-collapse" />
                )}
              </div>
            </div>
            <div className="col hidden-on-md table-header">
              <div>{t('formulaDatagridHeader')}</div>
            </div>
            <div className="col hidden-on-xxl hidden-on-md table-header">
              <div>{t('concentrationDatagridHeader')} |</div>
              <div>{t('temperatureDatagridHeader')}</div>
            </div>
            <div className="col hidden-up-to-xl table-header">
              <div>{t('concentrationDatagridHeader')}</div>
            </div>
            <div className="col hidden-up-to-xl table-header">
              <div>{t('temperatureDatagridHeader')}</div>
            </div>
            <div className="col hidden-on-375 table-header">
              <div
                className={
                  sortBy === 'MATERIAL'
                    ? sortDirection === SORT_ASCENDING
                      ? 'MATERIAL textOneLine order material-order-active'
                      : 'MATERIAL textOneLine order material-order-active-order-descending'
                    : 'MATERIAL textOneLine order'
                }
                onClick={handleClick}
              >
                {t('materialFilter')}

                {sortDirection === SORT_ASCENDING ? (
                  <span className="MATERIAL fwe-icon fwe-icon-arrows-expand" />
                ) : (
                  <span className="MATERIAL fwe-icon fwe-icon-arrows-collapse" />
                )}
              </div>
            </div>
            <div className="col hidden-from-1024 table-header">
              <div>{t('materialCategoryDatagridHeader')}</div>
            </div>
            <div className="col hide-to-xl hidden-on-375 table-header">
              {t('resistanceDatagridHeader')}
              <span
                className=""
                onClick={() =>
                  econdaClickEvent('"resistance" table head', 'table', 'tool tip', 'resistance categories tool tip')
                }
              >
                <InfoButtonPopup>
                  <div className="infobutton-popup-container">
                    <div className="infobutton-popup-header">{t('filterResistanceType')}</div>
                    {resistanceTypes.values.map(
                      (resistanceType) =>
                        resistanceType.id !== '-1' && (
                          <p className="micro-container" key={resistanceType.id}>
                            <img
                              alt={`${resistanceType.resistance} - ${t(resistanceType.translationKey)}`}
                              className="micrologo"
                              src={resistanceTypeImg[resistanceType.resistance]['SIZE_16']}
                            />
                            <span className="micrologo-text">
                              {resistanceType.resistance === 'CONDITIONALLY_RESISTANT'
                                ? t(resistanceType.translationKey) + '*'
                                : t(resistanceType.translationKey)}
                            </span>
                          </p>
                        ),
                    )}
                    <p className="fwe-pt-2 minimicro-text">*{t(infoButtonConditionallyResistanceType)}</p>
                  </div>
                </InfoButtonPopup>
              </span>
            </div>
          </Row>

          <div className="results-container">
            {!mediaResistances.isLoading &&
              mediaResistances.value.map((mediaResistance) => {
                const resistance = resistanceMap.get(mediaResistance.resistance);
                return (
                  <Row className="media-resistance-table-results">
                    <div className="col hidden-on-everything py-2">
                      <div className="result-media pr-1">{t(mediaResistance.media.translationKey)} </div>
                      <div className="result-formula pr-1">
                        {formatFormula(mediaResistance.media.formula ? mediaResistance.media.formula : '')}
                      </div>
                      <div>
                        <span className="result-concentration gray-text py-2">
                          {formatConcentration(
                            mediaResistance.media.concentration,
                            mediaResistance.media.translationKey,
                          )}{' '}
                          |
                        </span>
                        <span className="result-temperature gray-text py-2">
                          {formatTemperature(mediaResistance.media.temperature)}
                        </span>
                      </div>
                      <div className="result-material-container hide-from-1024 pt-2">
                        <div className="result-material">{t(mediaResistance.material.translationKey)}</div>
                        <div className="result-material-category gray-text">
                          {t(mediaResistance.material.materialCategory.translationKey)}
                        </div>
                      </div>
                    </div>
                    <div className="col hidden-on-md">
                      <div className="result-media py-2">{t(mediaResistance.media.translationKey)}</div>
                    </div>
                    <div className="col hidden-on-md">
                      <div className="result-formula py-2">
                        {formatFormula(mediaResistance.media.formula ? mediaResistance.media.formula : '')}
                      </div>
                    </div>
                    <div className="col hidden-on-xxl hidden-on-md py-2">
                      <span className="result-concentration gray-text">
                        {formatConcentration(mediaResistance.media.concentration, mediaResistance.media.translationKey)}
                      </span>
                      <span className="pipe-separator gray-text">|</span>
                      <span className="result-temperature gray-text">
                        {formatTemperature(mediaResistance.media.temperature)}
                      </span>
                    </div>
                    <div className="col hidden-up-to-xl hidden-on-md">
                      <div className="result-concentration gray-text py-2">
                        {formatConcentration(mediaResistance.media.concentration, mediaResistance.media.translationKey)}
                      </div>
                    </div>
                    <div className="col hidden-up-to-xl hidden-on-md">
                      <div className="result-temperature gray-text py-2">
                        {formatTemperature(mediaResistance.media.temperature)}
                      </div>
                    </div>
                    <div className="col hidden-on-md border-left">
                      <div className="result-material py-2 ">{t(mediaResistance.material.translationKey)}</div>
                    </div>
                    <div className="col hidden-on-md">
                      <div className="result-material-category gray-text py-2">
                        {t(mediaResistance.material.materialCategory.translationKey)}
                      </div>
                    </div>
                    <div className="col hidden-on-everything hidden-on-375 py-2 border-left">
                      <div className="result-material">{t(mediaResistance.material.translationKey)}</div>
                      <div className="result-material-category gray-text pl-2">
                        {t(mediaResistance.material.materialCategory.translationKey)}
                      </div>
                    </div>
                    <div className="col icon-column">
                      <div className="result-resistance-container py-2">
                        <img
                          alt="Result resitance help icon"
                          className="td-image"
                          src={showIcon(mediaResistance.resistance)}
                        ></img>
                        <div className="td-resistance hidden-up-to-xl">{t(resistance)}</div>
                      </div>
                    </div>
                  </Row>
                );
              })}
          </div>

          {!mediaResistances.isLoading && mediaResistances.value.length === 0 && (
            <NoResultsFound
              iconSize={'fwe-icon-4x'}
              noResultsTitle="noResultsFound"
              noResultsMessage="resultNoResultFirstSentence"
            />
          )}

          {mediaResistances.isLoading && (
            <div className="spinner-container">
              <LoadingIndicator size="large" />
            </div>
          )}
        </div>
      </div>
    </>
  );
});

export default MediaResistance;
